'use client'

import { AboutMenuDesktop } from '@/components/layout/AboutMenu'
import ThemeToggle from '@/components/layout/header/ThemeToggle'
import SubscribeModalTrigger from '@/components/modals/triggers/SubscribeModalTrigger'
import SearchButton from '@/components/search/SearchButton'
import { Button } from '@/components/ui/Button'
import { requestHomeReset } from '@/lib/home-filter'
import { TOP_NAV, isNavActive } from '@/lib/nav'
import { track } from '@/lib/tracking'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

// Desktop header (2026-11-01, header A of the mocks, "search in the bar"):
// one sticky row, the lockup at the left with the nav beside it, and at the
// right the search box, light / dark, Log in and Subscribe. It replaced
// header B (the centred masthead) the same day: with the page openers
// centred, a centred header over them repeated itself on every page (Sam),
// so the header sits off-centre and the openers keep the centre.
// Under 1280px the box gives way to the search icon. Phones use
// header/Header.jsx.
export default function TopBar({ userData }) {
  const pathname = usePathname()
  const isGuest = userData?.type === 'guest'

  return (
    <header className="rule-double sticky top-0 z-40 hidden bg-background/95 backdrop-blur md:block">
      <div className="page-wide flex h-[72px] items-center gap-7">
        <Link
          href="/"
          onClick={requestHomeReset}
          className="shrink-0 transition-opacity hover:opacity-80"
        >
          {/* The horizontal lockup; the reversed one on the dark theme. */}
          <Image
            alt="Pano News"
            src="/brand/pano-news-horizontal.svg"
            width={874}
            height={256}
            className="h-10 w-auto dark:hidden"
            priority
            unoptimized
          />
          <Image
            alt="Pano News"
            src="/brand/pano-news-horizontal-reversed.svg"
            width={874}
            height={256}
            className="hidden h-10 w-auto dark:block"
            priority
            unoptimized
          />
        </Link>

        <nav aria-label="Primary" className="flex items-center gap-[22px]">
          {TOP_NAV.map((item) => {
            const active = isNavActive(item.href, pathname)
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={active ? 'page' : undefined}
                onClick={() => {
                  track('nav_click', {
                    href: item.href,
                    label: item.label,
                    location: 'topbar',
                  })
                  if (item.href === '/') requestHomeReset()
                }}
                className={`py-1.5 font-sans text-sm text-foreground underline-offset-[6px] transition-colors ${
                  active ? 'font-semibold underline' : 'hover:underline'
                }`}
              >
                {item.label}
              </Link>
            )
          })}
          <AboutMenuDesktop />
        </nav>

        <div className="ml-auto flex items-center gap-2.5">
          <SearchButton location="topbar" as="bar" shortcuts />
          <ThemeToggle />
          {isGuest ? (
            <>
              <Link
                href="/login"
                className="whitespace-nowrap px-2 py-1.5 font-sans text-sm text-foreground underline-offset-[6px] hover:underline"
              >
                Log in
              </Link>
              {/* Outline, not solid (2026-09-30): the subscribe card on the
                  page is the primary ask; one solid button per action. */}
              <SubscribeModalTrigger
                size="sm"
                variant="outline"
                location="topbar"
                label="Subscribe"
              />
            </>
          ) : (
            <Button href="/account" variant="outline" size="sm">
              Account
            </Button>
          )}
        </div>
      </div>
    </header>
  )
}
